import { useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  LANGUAGES,
  LANGUAGE_NAMES,
  changeLanguage,
  currentLanguage,
  useTranslation,
} from '../i18n';
import { outside, useAnchoredPanel } from './anchored';
import { Icon } from './Icon';

/**
 * Переключатель языка в шапке (§11, фаза 10, задача 1).
 *
 * Рядом с переключателем темы, а не в отдельном разделе настроек: язык —
 * такое же свойство рабочего места, как тема, и ищут его там же. Списком,
 * а не кнопкой-переключателем, в отличие от темы: языков может стать
 * больше двух, и «нажми, чтобы узнать, что получится» на языке работает
 * хуже, чем на цвете, — прочитать чужой алфавит и вернуться обратно
 * заметно труднее, чем моргнуть светлой темой.
 *
 * Подпись каждого языка — на нём самом: «English» в русском меню ищут
 * глазами именно так, а не как «Английский».
 *
 * Список рисуется порталом и позиционируется от экрана (`useAnchoredPanel`).
 * Иначе он обрезается: на телефоне переключатель стоит внизу выезжающего
 * меню, а у того `overflow-y-auto` — контейнер, который клипует и вбок.
 */
export function LanguageSwitch(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { anchor, panel, style } = useAnchoredPanel<HTMLDivElement, HTMLUListElement>(open);

  const active = currentLanguage();

  useEffect(() => {
    if (!open) {
      return;
    }

    const onDown = (event: MouseEvent): void => {
      if (outside(event.target, anchor, panel)) {
        setOpen(false);
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, anchor, panel]);

  return (
    <div ref={anchor} className="relative">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('common.language.switch')}
        title={t('common.language.switch')}
        className="flex items-center gap-1 rounded-sm border border-line px-2 py-1 text-dim hover:text-fg"
      >
        <Icon name="globe" />
        <span className="uppercase">{active}</span>
      </button>

      {open
        ? createPortal(
            <ul
              role="listbox"
              ref={panel}
              aria-label={t('common.language.switch')}
              style={style}
              className="z-50 min-w-[9rem] rounded-sm border border-line bg-raised py-1 font-sans normal-case shadow-lg"
            >
              {LANGUAGES.map((language) => (
                <li key={language}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={language === active}
                    onClick={() => {
                      setOpen(false);
                      void changeLanguage(language);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-overlay ${
                      language === active ? 'font-medium text-fg' : 'text-muted'
                    }`}
                  >
                    <span className="w-8 font-mono text-2xs uppercase">{language}</span>
                    {LANGUAGE_NAMES[language]}
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
